import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { prefetchNodeDataProvidersData } from '@atlaskit/node-data-provider';
import { CardClient } from '@atlaskit/link-provider';
import type { SyncBlockInstance } from '@atlaskit/editor-synced-block-provider';
import { fetchReferences, getConfluencePageAri } from '@atlaskit/editor-synced-block-provider';

import { getSessionData as uninstrumentedGetSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { type SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { createEmojiProviderParameters } from '@confluence/fabric-emoji-support';
import { getEmojiNodeDataProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { createCookie } from '@confluence/content-smartlinks/entry-points/createCookie.ts';
import { expVal } from '@confluence/feature-experiments';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { fg } from '@confluence/feature-gating';

import {
	NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH,
	NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
} from './utils/nodeDataProvidersConstants.ts';
import type {
	AdfDocQuery as AdfDocQueryRes,
	AdfDocQueryVariables,
} from './queries/__types__/AdfDocQuery.ts';
import { AdfDocQuery } from './queries/AdfDocQuery.graphql.ts';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/editor/nodeDataProvidersData';
const DEFAULT_TIMEOUT = 300;

export async function preloadEditorNodeDataProvidersData(
	contentId: string | null,
	cloudId?: string,
	isBlog?: boolean,
): Promise<void> {
	if (!contentId) {
		return;
	}

	if (!isSSR()) {
		// If we are not in SSR, we don't need to preload node data providers data.
		return;
	}

	await SSRMeasures.run(MEASURE_SEGMENT, async () => {
		const fetchAdfStart = performance.now();
		let docJsonString: string | undefined;
		let syncBlocks: SyncBlockInstance[] | undefined;

		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			cloudId &&
			typeof isBlog !== 'undefined' &&
			expVal('platform_synced_block', 'isEnabled', false) &&
			fg('platform_synced_block_dogfooding')
		) {
			// we need both adf and sync block references here
			// so follow up queries can load data from both the main adf and nested adf from sync blocks
			const result = await fetchAdfWithSyncBlockReferences(contentId, cloudId, isBlog);
			docJsonString = result.adf;
			syncBlocks = result.syncBlockReferences;
		} else {
			docJsonString = await fetchAdf(contentId);
		}

		const fetchAdfDuration = performance.now() - fetchAdfStart;

		if (!docJsonString) {
			return;
		}

		const doc = JSON.parse(docJsonString);

		const providers: Parameters<typeof prefetchNodeDataProvidersData>[0]['providers'] = [];

		providers.push({
			provider: ssrEditorCardProvider(),
			maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.smartCard,
		});

		const sessionData = await getSessionData();
		const emojiNodeDataProvider = ssrEmojiNodeDataProvider(sessionData);
		if (emojiNodeDataProvider) {
			providers.push({
				provider: emojiNodeDataProvider,
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.emoji,
			});
		}

		providers.forEach(({ provider }) => {
			SSRMeasures.markMeasureStart(measureProviderSegment(provider.name));
		});

		const editorNodeDataProvidersData = await prefetchNodeDataProvidersData({
			doc,
			providers,
			timeout: DEFAULT_TIMEOUT - fetchAdfDuration,
			maxNodesToVisit: NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
		});

		Object.entries(editorNodeDataProvidersData).forEach(([providerName, { duration }]) => {
			SSRMeasures.setMeasureDuration(measureProviderSegment(providerName), duration);
		});

		window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] = Object.fromEntries(
			Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
				providerName,
				data,
			]),
		);

		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			syncBlocks &&
			expVal('platform_synced_block', 'isEnabled', false) &&
			fg('platform_synced_block_dogfooding')
		) {
			const existingNodeData = window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] || {};
			window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] = {
				...existingNodeData,
				syncBlocks,
			};
		}
	});
}

function fetchAdf(contentId: string | undefined | null): Promise<string | undefined> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/fetch:AdfDocQuery`, async () => {
		try {
			const result = await query<AdfDocQueryRes, AdfDocQueryVariables>({
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: AdfDocQuery,
				variables: {
					contentId,
					status: ['draft'],
				},
				fetchPolicy: 'network-only',
			});

			if (result.hasErrors) {
				return undefined;
			}

			return result.data?.singleContent?.body?.atlas_doc_format?.value || undefined;
		} catch {
			return undefined;
		}
	});
}

export function fetchSyncBlockReferences(contentId: string, cloudId: string, isBlog: boolean) {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/fetch:SyncBlockReferences`, async () => {
		const pageAri = getConfluencePageAri({
			cloudId,
			pageId: contentId,
			pageType: isBlog ? 'blogpost' : 'page',
		});
		try {
			const results = await fetchReferences(pageAri);

			if (!results || typeof results === 'string') {
				// has error return undefined
				return undefined;
			}

			return results as SyncBlockInstance[];
		} catch {
			return undefined;
		}
	});
}

export const fetchAdfWithSyncBlockReferences = async (
	contentId: string | undefined | null,
	cloudId: string,
	isBlog: boolean,
) => {
	const results = await Promise.allSettled([
		fetchAdf(contentId),
		fetchSyncBlockReferences(contentId || '', cloudId, isBlog),
	]);

	const [adfResult, syncBlockReferencesResult] = results;

	return {
		adf: adfResult.status === 'fulfilled' ? adfResult.value : undefined,
		syncBlockReferences:
			syncBlockReferencesResult.status === 'fulfilled'
				? syncBlockReferencesResult.value
				: undefined,
	};
};

function getSessionData(): Promise<SessionDataType> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/getSessionData`, async () => {
		return uninstrumentedGetSessionData();
	});
}

function ssrEmojiNodeDataProvider(sessionData: SessionDataType) {
	const { userId, cloudId, isLicensed, isLoggedIn } = sessionData;

	// We need a special emoji parameters that supports SSR load optimization here.
	const emojiResourceParams = createEmojiProviderParameters({
		cloudId,
		userId,
		isLicensed,
		isLoggedIn,
	});

	return getEmojiNodeDataProvider(EmojiResource, {
		...emojiResourceParams,
		allowAnonymousAccess: true,
	});
}

function ssrEditorCardProvider() {
	const { host, cloudId, userContext, customURLs } = getSSRRenderInputs();

	// This URL will be something like `https://object-resolver.staging.atl-paas.net/resolve/batch`.
	// We need only protocol and host to pass it to the EditorCardProvider,
	// because path will be added by `CardClient`.
	const objectResolverURL =
		customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

	// We need to define a custom CardClient for SSR, because the default one works through public API gateway.
	const ssrCardClient = new CardClient('custom', objectResolverURL);
	// Headers from `fetchORSForSSR` from confluence/next/packages/content-smartlinks/src/index.ts
	ssrCardClient.setHeaders({
		Cookie: createCookie(userContext),
		Origin: host.startsWith('http') ? host : `https://${host}`,
		CloudId: cloudId,
	});

	return new EditorCardProvider(
		'custom',
		objectResolverURL,
		'CONFLUENCE',
		undefined,
		ssrCardClient,
	);
}

function isSSR(): boolean {
	try {
		return Boolean(
			// In most places there is no document when running on server-side
			typeof document === 'undefined' ||
			// When SSRing editor with full cycle mode we define the document
			// Check Confluence specific environment variable
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			(typeof process !== 'undefined' && process.env.REACT_SSR),
		);
	} catch {
		// Catch possible error that might occur and just return false
		return false;
	}
}

function measureProviderSegment(providerName: string) {
	return `${MEASURE_SEGMENT}/${providerName}`;
}
